import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { UserPlus, Phone, EnvelopeSimple, Paperclip, BellRinging } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  new: "bg-indigo-50 text-indigo-700 border-indigo-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  won: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lost: "bg-zinc-100 text-zinc-500 border-zinc-200",
};
const NEXT = { new: ["contacted", "lost"], contacted: ["won", "lost"], won: [], lost: ["contacted"] };
const LABEL = { contacted: "Mark contacted", won: "Won", lost: "Lost" };

export function LeadsPipeline() {
  const qc = useQueryClient();
  const { data: leads } = useQuery({ queryKey: ["leads"], queryFn: () => api.get("/leads").then((r) => r.data) });
  const update = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/leads/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const active = (leads || []).filter((l) => l.status !== "lost");
  const counts = (leads || []).reduce((a, l) => ({ ...a, [l.status]: (a[l.status] || 0) + 1 }), {});

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="leads-pipeline">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><UserPlus size={15} /> Website leads</h3>
        <span className="text-[10px] text-zinc-400 font-mono">{counts.new || 0} new · {counts.contacted || 0} contacted · {counts.won || 0} won</span>
      </div>
      {!leads?.length && <p className="text-xs text-zinc-400 italic" data-testid="leads-empty">No website leads yet — they'll appear here the moment your contact form is submitted.</p>}
      <div className="space-y-2">
        {(leads || []).map((l) => (
          <div key={l.id} className="rounded-md border border-zinc-100 bg-zinc-50/60 p-3" data-testid={`lead-row-${l.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-zinc-900">{l.name} <span className={`text-[9px] uppercase border rounded-sm px-1.5 py-0.5 ml-1 ${STATUS[l.status] || STATUS.new}`}>{l.status}</span></p>
                <p className="text-[11px] text-zinc-500 flex flex-wrap items-center gap-x-3 mt-0.5">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-zinc-800"><EnvelopeSimple size={11} />{l.email}</a>
                  {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-zinc-800"><Phone size={11} />{l.phone}</a>}
                  {l.service && <span className="text-zinc-400">{l.service}</span>}
                  {l.source_page && <span className="text-[9px] bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-sm px-1.5 py-0.5" data-testid={`lead-source-${l.id}`}>from: {l.source_page}</span>}
                  {l.attachment && (
                    <a href={`${API_BASE}/api/leads/${l.id}/attachment`} target="_blank" rel="noreferrer" data-testid={`lead-attachment-${l.id}`}
                      className="flex items-center gap-1 text-[9px] bg-sky-50 text-sky-700 border border-sky-100 rounded-sm px-1.5 py-0.5 hover:bg-sky-100">
                      <Paperclip size={10} />{l.attachment.filename}
                    </a>
                  )}
                  {l.nudge_sent && l.status === "new" && (
                    <span className="flex items-center gap-1 text-[9px] bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-1.5 py-0.5" data-testid={`lead-nudge-${l.id}`}>
                      <BellRinging size={10} />needs follow-up (2+ days)
                    </span>
                  )}
                </p>
                {l.message && <p className="text-[11px] text-zinc-600 mt-1 line-clamp-2">{l.message}</p>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {(NEXT[l.status] || []).map((s) => (
                  <button key={s} data-testid={`lead-${s}-btn-${l.id}`} disabled={update.isPending} onClick={() => update.mutate({ id: l.id, status: s })}
                    className={`text-[10px] border rounded-sm px-2 py-0.5 transition-colors duration-150 ${s === "won" ? "text-emerald-700 border-emerald-200 hover:bg-emerald-50" : s === "lost" ? "text-zinc-500 border-zinc-200 hover:bg-zinc-100" : "text-amber-700 border-amber-200 hover:bg-amber-50"}`}>
                    {LABEL[s]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
